import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { listDevices, Device } from '../../api/device';
import { useCan } from '../../store';
import { listProcessArchs, ProcessArch } from '../../api/parameter';
import { listGalleryFacets, ListGalleryParams } from '../../api/gallery';
import { useSyncQueryToUrl } from '../../utils/urlState';

// 效果图 / 矩阵图 两个 tab 共用的筛选状态 (2026-07-31 抽公共):
// 关键词 + 工艺架构 + 机型/型号 + 加工类型 + 激光类型 + 材质(?xcode= 链接预填),
// 全部同步进 URL query (进详情后浏览器后退可还原); UI 见 GalleryFilterBar.

export const ARCH_ALL = 'all';

export interface GalleryFilterCtl {
    archs: ProcessArch[];
    archCode: string;
    family: string;
    deviceKey: string;
    xcode: string;
    keyword: string;
    processType: string;
    lightSource: string;
    type1: string;
    type2: string;
    page: number;
    devices: Device[];
    modeOpts: string[];
    lightOpts: string[];
    setFamily: (v: string) => void;
    setDeviceKey: (v: string) => void;
    setXcode: (v: string) => void;
    setKeyword: (v: string) => void;
    setProcessType: (v: string) => void;
    setLightSource: (v: string) => void;
    setTypes: (t1: string, t2: string) => void;
    setPage: (v: number) => void;
    onSetArch: (code: string) => void;
    onReset: () => void;
    /** listGallery 用的查询参数 (不含 hasMatrix/page/pageSize, 调用方自己补) */
    query: Omit<ListGalleryParams, 'hasMatrix' | 'page' | 'pageSize'>;
}

/** path = 当前 tab 的路由 ('/gallery' | '/gallery/matrix'), 用于 URL 写回. */
export function useGalleryFilter(path: string): GalleryFilterCtl {
    const canSeeDevice = useCan('device', 'get');
    const location = useLocation();
    const initQ = new URLSearchParams(location.search);

    const [archs, setArchs] = useState<ProcessArch[]>([]);
    const [archCode, setArchCode] = useState<string>(
        initQ.get('arch') || ARCH_ALL,
    );
    // 机型 (family) 与 型号 (deviceKey) 分开筛 — 后端 family 走 sub-query 反查 device 表.
    const [family, setFamily] = useState(initQ.get('family') || '');
    const [deviceKey, setDeviceKey] = useState(initQ.get('deviceKey') || '');
    const [xcode, setXcode] = useState(initQ.get('xcode') || '');
    // 关键词: 后端模糊搜 xcode / device_key / note
    const [keyword, setKeyword] = useState(initQ.get('keyword') || '');
    const [processType, setProcessType] = useState(
        initQ.get('processType') || '',
    );
    const [lightSource, setLightSource] = useState(
        initQ.get('lightSource') || '',
    );
    // 按所属材料一/二级分类过滤 (效-02)
    const [type1, setType1] = useState(initQ.get('type1') || '');
    const [type2, setType2] = useState(initQ.get('type2') || '');
    const [page, setPage] = useState(Number(initQ.get('page')) || 1);
    // 设备候选: 直接从 device 表拉, 拿到真正的 family + deviceKey 配对; 加工类型仍走 gallery facets.
    const [devices, setDevices] = useState<Device[]>([]);
    const [modeOpts, setModeOpts] = useState<string[]>([]);
    const [lightOpts, setLightOpts] = useState<string[]>([]);

    // 启动拉架构
    useEffect(() => {
        listProcessArchs()
            .then((l) => setArchs(l))
            .catch(() => setArchs([]));
    }, []);

    // 拉 facets (加工类型) + device 表 (机型/型号) 候选. 切架构时两边都重置.
    useEffect(() => {
        const ac = archCode === ARCH_ALL ? '' : archCode;
        listGalleryFacets(ac)
            .then((f) => {
                setModeOpts(f.processTypes || []);
                setLightOpts(f.lightSources || []);
            })
            .catch(() => {
                setModeOpts([]);
                setLightOpts([]);
            });
        // 设备筛选只在有设备查看权时拉 (2026-09-13): 效果图管理员没有 device get,
        // 没有清单时下拉自然为空, 效果图卡片仍按行自带的 deviceKey 显示.
        if (!canSeeDevice) {
            setDevices([]);
        } else if (ac) {
            listDevices({ archCode: ac, pageSize: 200 })
                .then((r) => setDevices(r.list || []))
                .catch(() => setDevices([]));
        } else {
            // "全部"视图: 不限架构, 拉所有设备
            listDevices({ pageSize: 500 })
                .then((r) => setDevices(r.list || []))
                .catch(() => setDevices([]));
        }
    }, [archCode, canSeeDevice]);

    useSyncQueryToUrl(path, {
        arch: archCode === ARCH_ALL ? '' : archCode,
        family,
        deviceKey,
        xcode,
        keyword,
        processType,
        lightSource,
        type1,
        type2,
        page,
    });

    // 切架构时, 设备/模式过滤需重置 (避免跨架构残留); 材料过滤保留
    const onSetArch = (code: string) => {
        setArchCode(code);
        setFamily('');
        setDeviceKey('');
        setProcessType('');
        setPage(1);
    };

    const setTypes = (t1: string, t2: string) => {
        setType1(t1);
        setType2(t2);
        setPage(1);
    };

    const onReset = () => {
        setKeyword('');
        setArchCode(ARCH_ALL);
        setFamily('');
        setDeviceKey('');
        setXcode('');
        setProcessType('');
        setLightSource('');
        setType1('');
        setType2('');
        setPage(1);
    };

    return {
        archs,
        archCode,
        family,
        deviceKey,
        xcode,
        keyword,
        processType,
        lightSource,
        type1,
        type2,
        page,
        devices,
        modeOpts,
        lightOpts,
        setFamily,
        setDeviceKey,
        setXcode,
        setKeyword,
        setProcessType,
        setLightSource,
        setTypes,
        setPage,
        onSetArch,
        onReset,
        query: {
            keyword: keyword || undefined,
            archCode: archCode === ARCH_ALL ? '' : archCode,
            deviceKey,
            // 仅选机型时传 family; 选了型号优先按 deviceKey 精确匹配.
            family: !deviceKey ? family || undefined : undefined,
            xcode,
            processType,
            lightSource: lightSource || undefined,
            type1: type1 || undefined,
            type2: type2 || undefined,
        },
    };
}
